import { DEFAULT_DEMO_ID } from '@/demo/defaultDemoConfig';
import { tokenName } from '@/utils/token';

const DEMO_SESSION_KEY = 'demoId';
const DEMO_PARAM = 'demo';

export interface DemoSupplier {
  firstName: string;
  lastName: string;
  email: string;
  authServiceUuid: string;
}

/**
 * Builds a structurally valid (but unsigned) JWT from the demo supplier config
 * and writes it to localStorage under VITE_TOKEN_NAME.
 */
export function writeDemoJwt(supplier: DemoSupplier): void {
  const nowSeconds = Math.floor(Date.now() / 1000);
  const oneDaySeconds = 1 * 24 * 60 * 60;

  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    payload: {
      type: 'user_auth',
      access: 'supplier',
      user: {
        uuid: supplier.authServiceUuid,
        sessionUuid: supplier.authServiceUuid,
        isAccountLocked: false,
        source: 'demo',
        datacenters: [],
        isSource: true,
        emailAddress: supplier.email,
        lastLogin: new Date().toISOString(),
        hasPassword: false,
        needsPasswordReset: false,
        features: [],
      },
    },
    iat: nowSeconds,
    exp: nowSeconds + oneDaySeconds,
    iss: 'demo',
  };

  const encode = (obj: object) => btoa(JSON.stringify(obj)).replace(/=+$/, '');
  const fakeJwt = `${encode(header)}.${encode(payload)}.demo-signature`;

  localStorage.setItem(tokenName, fakeJwt);
}

/**
 * Demo mode is permitted in the dedicated demo environment and fss (lower demo env for development).
 * Blocked in uat and prod to prevent MSW from intercepting real API calls.
 */
export function isDemoModeAllowed(): boolean {
  return import.meta.env.VITE_ENV === 'demo' || import.meta.env.VITE_ENV === 'fss';
}

/**
 * Returns true if the app is currently running in demo mode.
 * Checks URL param first, then falls back to sessionStorage for page-refresh survival.
 */
export function isDemoModeActive(): boolean {
  if (!isDemoModeAllowed()) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.has(DEMO_PARAM) || !!sessionStorage.getItem(DEMO_SESSION_KEY);
}

/**
 * Returns the active demo UUID from URL param or sessionStorage.
 * Returns null if demo mode is not active or no UUID is present.
 */
export function getActiveDemoId(): string | null {
  if (!isDemoModeAllowed()) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);

  if (params.has(DEMO_PARAM)) {
    return params.get(DEMO_PARAM) || DEFAULT_DEMO_ID;
  }

  return sessionStorage.getItem(DEMO_SESSION_KEY);
}

function persistDemoSession(demoId: string): void {
  sessionStorage.setItem(DEMO_SESSION_KEY, demoId);
}

async function startMswWorker(demoId: string): Promise<void> {
  const { worker } = await import('@/demo/demoBrowser');
  const { loadDemoHandlers } = await import('@/demo/demoHandlers');
  const { installDemoDownloadInterceptor } = await import('@/demo/demoDownloadHandlers');

  const { supplier, handlers } = await loadDemoHandlers(demoId);
  writeDemoJwt(supplier);
  worker.use(...handlers);
  installDemoDownloadInterceptor();

  // The worker is served from public/ at `${VITE_BASE_PATH}/mockServiceWorker.js`,
  // so its scope is the base path. In the demo env (base '/') that's root and
  // controls everything. Under local fss (base '/enterprise') the scope is
  // '/enterprise/', which only controls pages at or below the trailing slash —
  // so demo must be launched from /enterprise/?demo=<id>, not bare /enterprise.
  const basePath = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '/';
  const swUrl = `${basePath.replace(/\/$/, '')}/mockServiceWorker.js`;
  try {
    await worker.start({
      serviceWorker: { url: swUrl },
      onUnhandledRequest: 'warn',
      quiet: true,
    });
  } catch (error) {
    console.error('Failed to start demo MSW worker', { demoId, error });
  }
}

/**
 * Boots MSW for an active demo session when permitted.
 * Never rejects — demo setup failures are logged and the app continues without mocks.
 *
 * ?demo=<uuid>  → fetches that config and boots dynamic demo handlers
 * ?demo         → loads bundled DEFAULT_DEMO_CONFIG
 * No ?demo param → no-op unless sessionStorage has a persisted demo id (refresh)
 */
export async function enableDemoMode(): Promise<void> {
  if (!isDemoModeAllowed()) {
    return;
  }

  const basePath = import.meta.env.VITE_BASE_PATH ?? '';
  const onBasePath = !basePath || window.location.pathname.startsWith(basePath);
  if (!onBasePath) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const willBootDemo = params.has(DEMO_PARAM) || !!sessionStorage.getItem(DEMO_SESSION_KEY);

  // The MSW worker is served at `${base}/mockServiceWorker.js`, giving it scope
  // `${base}/` (e.g. /enterprise/). That scope controls /enterprise/ and below
  // but NOT the slash-less /enterprise, so a demo launched from bare /enterprise
  // is uncontrolled and its fetches escape to the real gateway. Redirect to the
  // trailing-slash path so the worker's natural scope controls the page — no
  // server header or base-path change needed. Only runs once per demo entry; the
  // reload's assets are cached, and it is a no-op when the slash is already present.
  if (willBootDemo && basePath && basePath !== '/' && window.location.pathname === basePath) {
    window.location.replace(`${basePath}/${window.location.search}`);
    return;
  }

  if (!params.has(DEMO_PARAM)) {
    // Not requested via URL — check sessionStorage (page refresh case)
    const storedId = sessionStorage.getItem(DEMO_SESSION_KEY);
    if (!storedId) {
      return;
    }

    await startMswWorker(storedId);
    return;
  }

  const demoId = params.get(DEMO_PARAM) || DEFAULT_DEMO_ID;

  persistDemoSession(demoId);
  await startMswWorker(demoId);
}
